import { useMemo } from "react";

import moment, { Moment } from "moment";

import { useGetDataSubmissionDates, useGetFarmPayoutApprovals, useGetFarms } from "utils";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

export const useSplitFieldDateRestriction = () => {
  const { currentFarm } = useGetFarms();
  const farmId = currentFarm?.id;

  const { submissionStatusValue } = useSubmissionStatus();
  const { data: dataSubmissionDates } = useGetDataSubmissionDates({ farmId });
  const { data: payoutApprovals, isLoading } = useGetFarmPayoutApprovals();

  return useMemo(() => {
    const approvedYears = new Set(payoutApprovals?.map((approval) => approval.year));

    // activeSubmissionYear = the currently active cycle's year, distinct from useSubmissionStatus's
    // own reporting-year concept (they roll over at different points in time).
    const activeSubmissionYear = dataSubmissionDates?.submissionStartDate ? moment(dataSubmissionDates.submissionStartDate).year() : moment().year();

    const lastSubmissionYear = submissionStatusValue?.lastSubmissionYear ?? null;
    const lastSubmissionDate = submissionStatusValue?.lastSubmissionDate ? moment(submissionStatusValue.lastSubmissionDate) : null;
    const payoutApproved = lastSubmissionYear != null && approvedYears.has(lastSubmissionYear);

    // Mirrors the backend's 403 gate: only blocks outright once the farm has (re)submitted for the
    // active cycle. Date-consistency checks below still apply against the last submission regardless.
    const hasSubmissionForActiveYear = lastSubmissionYear === activeSubmissionYear;

    // A previously payout-approved submission is finalized: no split date may fall at or before it,
    // even if a later, not-yet-approved submission's own rule would otherwise allow it.
    const mostRecentApprovedSubmission = (submissionStatusValue?.submissions ?? [])
      .filter((submission) => approvedYears.has(submission.year))
      .sort((a, b) => b.year - a.year)[0];
    // Use updatedAt to mirror logic in endpoint for useSubmissionStatus
    const approvedFloorDate = mostRecentApprovedSubmission ? moment(mostRecentApprovedSubmission.updatedAt) : null;

    const isDisabledDate = (current: Moment) => {
      const isFuture = current.isAfter(moment().endOf("day"));
      if (isFuture) return true;
      if (approvedFloorDate && current.isSameOrBefore(approvedFloorDate, "day")) return true;
      if (!lastSubmissionDate) return false;
      return payoutApproved ? current.isSameOrBefore(lastSubmissionDate, "day") : current.isAfter(lastSubmissionDate, "day");
    };

    return { isDisabledDate, hasSubmissionForActiveYear, payoutApproved, loading: isLoading };
  }, [dataSubmissionDates, submissionStatusValue, payoutApprovals, isLoading]);
};
